import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';

const initialFormData = {
    visitaId:          0,
    unidadFuncionalId: null,
    nombreVisitante:   '',
    tipoVisitaId:      null,
    patente:           '',
    empresa:           '',
    fechaVisita:       null,
    observaciones:     '',
    tipoVehiculoId:           null,
    dniConductor:             '',
    aseguradora:              '',
    nroPoliza:                '',
    fechaPoliza:              null,
    fechaVencimientoSeguro:   null,
    registro:                 '',
    fechaVencimientoRegistro: null,
};

const vencidoBadge = (fecha) => {
    if (!fecha) return null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    if (new Date(fecha) >= hoy) return null;
    return (
        <span style={{
            background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca',
            fontWeight: 600, fontSize: '0.7rem', padding: '0.1rem 0.5rem', borderRadius: 99, marginLeft: 6,
        }}>
            Vencido
        </span>
    );
};

const Visitas = () => {
    const toast    = useRef(null);
    const fileRef  = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const usuarioId = (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.usuarioId || 0; } catch { return 0; } })();

    const [formData,      setFormData]      = useState(initialFormData);
    const [visible,       setVisible]       = useState(false);
    const [loading,       setLoading]       = useState(false);
    const [saving,        setSaving]        = useState(false);
    const [visitas,       setVisitas]       = useState([]);
    const [tiposVisita,   setTiposVisita]   = useState([]);
    const [tiposVehiculo, setTiposVehiculo] = useState([]);
    const [ufs,           setUfs]           = useState([]);
    const [pendingFiles,  setPendingFiles]  = useState([]);
    const [adjuntos,      setAdjuntos]      = useState([]);
    const [dragOver,      setDragOver]      = useState(false);

    const handleChange = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const isProfesional = tiposVisita.find(t => t.tipoVisitaId === formData.tipoVisitaId)?.descripcion?.toLowerCase() === 'profesional';

    // ─── Autocompletar por DNI (visitante frecuente) ──────────────────────────────

    const buscarVisitanteFrecuente = async (dni) => {
        const value = (dni || '').trim();
        if (!value || !barrio?.id) return;
        try {
            const { data } = await axios.get(`${HOST_API}api/visitanteFrecuente/BuscarPorDni`, {
                params: { administracionId, barrioId: barrio.id, dniConductor: value },
            });
            if (!data) return;
            setFormData(prev => ({
                ...prev,
                nombreVisitante:          prev.nombreVisitante || data.nombreVisitante || prev.nombreVisitante,
                patente:                  prev.patente || data.patente || prev.patente,
                tipoVehiculoId:           prev.tipoVehiculoId ?? data.tipoVehiculoId ?? prev.tipoVehiculoId,
                aseguradora:              prev.aseguradora || data.aseguradora || prev.aseguradora,
                nroPoliza:                prev.nroPoliza || data.nroPoliza || prev.nroPoliza,
                fechaPoliza:              prev.fechaPoliza || (data.fechaPoliza ? new Date(data.fechaPoliza) : prev.fechaPoliza),
                fechaVencimientoSeguro:   prev.fechaVencimientoSeguro || (data.fechaVencimientoSeguro ? new Date(data.fechaVencimientoSeguro) : prev.fechaVencimientoSeguro),
                registro:                 prev.registro || data.registro || prev.registro,
                fechaVencimientoRegistro: prev.fechaVencimientoRegistro || (data.fechaVencimientoRegistro ? new Date(data.fechaVencimientoRegistro) : prev.fechaVencimientoRegistro),
            }));
            toast.current.show({ severity: 'info', summary: 'Datos encontrados', detail: 'Se completaron los datos guardados de una visita anterior con este DNI', life: 3500 });
        } catch {
            // búsqueda opcional: si falla no bloquea la carga manual
        }
    };

    // ─── Fetch ───────────────────────────────────────────────────────────────────

    const fetchVisitas = useCallback(async () => {
        if (!barrio?.id || !usuarioId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/visitas/GetVisitas?administracionId=${administracionId}&barrioId=${barrio.id}&usuarioId=${usuarioId}`);
            setVisitas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las visitas', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, usuarioId, administracionId]);

    useEffect(() => { fetchVisitas(); }, [fetchVisitas]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/tipovisita/GetTipoVisitas`).then(({ data }) => setTiposVisita(data)).catch(() => {});
        axios.get(`${HOST_API}api/tipovehiculo/GetTipoVehiculos`).then(({ data }) => setTiposVehiculo(data)).catch(() => {});
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data)).catch(() => {});
    }, [HOST_API, barrio?.id, usuarioId]);

    // ─── Archivos ────────────────────────────────────────────────────────────────

    const addFiles = (fileList) => {
        const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        const nuevos  = Array.from(fileList).filter(f => allowed.includes(f.type));
        if (nuevos.length < fileList.length)
            toast.current.show({ severity: 'warn', summary: 'Tipo no permitido', detail: 'Solo se permiten PDF, JPG y PNG', life: 3000 });
        setPendingFiles(prev => [...prev, ...nuevos]);
    };

    const removePending = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

    const removeAdjunto = (adjuntoVisitaId) => {
        confirmDialog({
            message: '¿Eliminar este adjunto?',
            header: 'Confirmar',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/visitas/DeleteAdjunto?adjuntoVisitaId=${adjuntoVisitaId}`);
                    setAdjuntos(prev => prev.filter(a => a.adjuntoVisitaId !== adjuntoVisitaId));
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Adjunto eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el adjunto', life: 3000 });
                }
            },
        });
    };

    // ─── Submit ──────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Atención', detail: `Seleccioná ${t.unUnidadFuncional}`, life: 3000 }); return; }
        if (!formData.nombreVisitante.trim()) { toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingresá el nombre del visitante', life: 3000 }); return; }
        if (!formData.tipoVisitaId) { toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná el tipo de visita', life: 3000 }); return; }
        if (!formData.fechaVisita)  { toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná fecha y hora de la visita', life: 3000 }); return; }

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('model', JSON.stringify({
                ...formData,
                administracionId,
                barrioId:         barrio.id,
                usuarioId,
                empresa:          isProfesional ? formData.empresa : null,
            }));
            pendingFiles.forEach(f => fd.append('files', f));

            await axios.post(`${HOST_API}api/visitas/SaveVisita`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });

            setVisible(false);
            fetchVisitas();
            toast.current.show({ severity: 'success', summary: formData.visitaId ? 'Actualizado' : 'Registrado', detail: 'Visita guardada correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la visita', life: 3000 });
        } finally {
            setSaving(false);
        }
    };

    // ─── Edit / Delete ────────────────────────────────────────────────────────────

    const handleEdit = async (row) => {
        setFormData({
            visitaId:          row.visitaId,
            unidadFuncionalId: row.unidadFuncionalId,
            nombreVisitante:   row.nombreVisitante || '',
            tipoVisitaId:      row.tipoVisitaId,
            patente:           row.patente || '',
            empresa:           row.empresa || '',
            fechaVisita:       row.fechaVisita ? new Date(row.fechaVisita) : null,
            observaciones:     row.observaciones || '',
            tipoVehiculoId:           row.tipoVehiculoId || null,
            dniConductor:             row.dniConductor || '',
            aseguradora:              row.aseguradora || '',
            nroPoliza:                row.nroPoliza || '',
            fechaPoliza:              row.fechaPoliza ? new Date(row.fechaPoliza) : null,
            fechaVencimientoSeguro:   row.fechaVencimientoSeguro ? new Date(row.fechaVencimientoSeguro) : null,
            registro:                 row.registro || '',
            fechaVencimientoRegistro: row.fechaVencimientoRegistro ? new Date(row.fechaVencimientoRegistro) : null,
        });
        setPendingFiles([]);
        setAdjuntos([]);
        if (row.cantAdjuntos > 0) {
            try {
                const { data } = await axios.get(`${HOST_API}api/visitas/GetAdjuntosByVisita?visitaId=${row.visitaId}`);
                setAdjuntos(data);
            } catch {}
        }
        setVisible(true);
    };

    const handleDelete = (visitaId) => {
        confirmDialog({
            message: '¿Estás seguro de cancelar esta visita?',
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/visitas/DeleteVisita?visitaId=${visitaId}`);
                    fetchVisitas();
                    toast.current.show({ severity: 'success', summary: 'Cancelada', detail: 'Visita cancelada correctamente', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cancelar la visita', life: 3000 });
                }
            },
        });
    };

    const openNew = () => { setFormData(initialFormData); setPendingFiles([]); setAdjuntos([]); setVisible(true); };

    // ─── Render helpers ───────────────────────────────────────────────────────────

    const fmtFecha = (row) => row.fechaVisita ? new Date(row.fechaVisita).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '';

    const adjBadge = (row) => row.cantAdjuntos > 0
        ? <Badge value={row.cantAdjuntos} severity="info" />
        : <span style={{ color: '#aaa' }}>—</span>;

    const formatSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

    const HOST = process.env.REACT_APP_URL?.replace(/\/+$/, '');

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Mis Visitas</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Avisar visita" icon="pi pi-plus" onClick={openNew} />
            </div>

            {/* ─── Modal ─────────────────────────────────────────────────────────── */}
            <Dialog
                header={formData.visitaId ? 'Editar visita' : 'Avisar visita'}
                visible={visible}
                style={{ width: '640px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-wrap gap-3 mt-2">

                        <div className="w-full md:w-5">
                            <label style={lbl}>{t.UnidadFuncional} <span style={req}>*</span></label>
                            <Dropdown
                                value={formData.unidadFuncionalId}
                                options={ufs}
                                optionLabel="unidadFuncional"
                                optionValue="unidadFuncionalId"
                                onChange={(e) => handleChange('unidadFuncionalId', e.value)}
                                placeholder="Seleccioná UF..."
                                className="p-inputtext-sm w-full"
                                filter
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Fecha y hora de visita <span style={req}>*</span></label>
                            <Calendar
                                value={formData.fechaVisita}
                                onChange={(e) => handleChange('fechaVisita', e.value)}
                                showTime
                                hourFormat="24"
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Nombre del visitante <span style={req}>*</span></label>
                            <InputText
                                value={formData.nombreVisitante}
                                onChange={(e) => handleChange('nombreVisitante', e.target.value)}
                                className="p-inputtext-sm w-full"
                                placeholder="Ej: Juan Pérez"
                                maxLength={200}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Tipo de visita <span style={req}>*</span></label>
                            <Dropdown
                                value={formData.tipoVisitaId}
                                options={tiposVisita}
                                optionLabel="descripcion"
                                optionValue="tipoVisitaId"
                                onChange={(e) => handleChange('tipoVisitaId', e.value)}
                                placeholder="Seleccioná tipo..."
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Patente</label>
                            <InputText
                                value={formData.patente}
                                onChange={(e) => handleChange('patente', e.target.value)}
                                className="p-inputtext-sm w-full"
                                placeholder="Ej: AB 123 CD"
                                maxLength={20}
                            />
                        </div>

                        <div className="w-full">
                            <label style={{ ...lbl, fontWeight: 700, marginTop: '0.5rem' }}>Datos del vehículo (opcional)</label>
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Tipo de vehículo</label>
                            <Dropdown
                                value={formData.tipoVehiculoId}
                                options={tiposVehiculo}
                                optionLabel="descripcion"
                                optionValue="tipoVehiculoId"
                                onChange={(e) => handleChange('tipoVehiculoId', e.value)}
                                placeholder="Seleccioná tipo..."
                                className="p-inputtext-sm w-full"
                                showClear
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>DNI del conductor</label>
                            <InputText
                                value={formData.dniConductor}
                                onChange={(e) => handleChange('dniConductor', e.target.value)}
                                onBlur={(e) => buscarVisitanteFrecuente(e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={20}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Registro (licencia de conducir)</label>
                            <InputText
                                value={formData.registro}
                                onChange={(e) => handleChange('registro', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={50}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Vencimiento del registro</label>
                            <Calendar
                                value={formData.fechaVencimientoRegistro}
                                onChange={(e) => handleChange('fechaVencimientoRegistro', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Aseguradora</label>
                            <InputText
                                value={formData.aseguradora}
                                onChange={(e) => handleChange('aseguradora', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={150}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>N° de póliza</label>
                            <InputText
                                value={formData.nroPoliza}
                                onChange={(e) => handleChange('nroPoliza', e.target.value)}
                                className="p-inputtext-sm w-full"
                                maxLength={50}
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Fecha de póliza</label>
                            <Calendar
                                value={formData.fechaPoliza}
                                onChange={(e) => handleChange('fechaPoliza', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="w-full md:w-5">
                            <label style={lbl}>Vencimiento del seguro</label>
                            <Calendar
                                value={formData.fechaVencimientoSeguro}
                                onChange={(e) => handleChange('fechaVencimientoSeguro', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        {isProfesional && (
                            <div className="w-full md:w-5">
                                <label style={lbl}>Empresa</label>
                                <InputText
                                    value={formData.empresa}
                                    onChange={(e) => handleChange('empresa', e.target.value)}
                                    className="p-inputtext-sm w-full"
                                    placeholder="Nombre de la empresa"
                                    maxLength={200}
                                />
                            </div>
                        )}

                        <div className="w-full">
                            <label style={lbl}>Observaciones</label>
                            <InputTextarea
                                value={formData.observaciones}
                                onChange={(e) => handleChange('observaciones', e.target.value)}
                                rows={2}
                                autoResize
                                className="w-full p-inputtext-sm"
                                maxLength={500}
                            />
                        </div>

                        {/* ─── Adjuntos existentes (edit) ─────────────────────── */}
                        {adjuntos.length > 0 && (
                            <div className="w-full">
                                <label style={lbl}>Adjuntos actuales</label>
                                <div className="flex flex-column gap-2">
                                    {adjuntos.map(a => (
                                        <div key={a.adjuntoVisitaId} style={fileRow}>
                                            <i className="pi pi-file" style={{ color: '#667085' }} />
                                            <a href={`${HOST}${a.rutaArchivo}`} target="_blank" rel="noreferrer"
                                               style={{ flex: 1, fontSize: '0.85rem', color: '#1570ef', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {a.nombreArchivo}
                                            </a>
                                            <button type="button" onClick={() => removeAdjunto(a.adjuntoVisitaId)} style={xBtn}>
                                                <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ─── Upload nuevos ──────────────────────────────────── */}
                        <div className="w-full">
                            <label style={lbl}>Adjuntar documentación</label>
                            <div
                                onClick={() => fileRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                                style={{
                                    border: `2px dashed ${dragOver ? '#1570ef' : '#d0d5dd'}`,
                                    borderRadius: 10, padding: '1rem 1.5rem', textAlign: 'center',
                                    cursor: 'pointer', background: dragOver ? '#f0f6ff' : '#fafafa',
                                }}
                            >
                                <i className="pi pi-cloud-upload" style={{ fontSize: '1.5rem', color: dragOver ? '#1570ef' : '#98a2b3', display: 'block', marginBottom: '0.4rem' }} />
                                <span style={{ fontSize: '0.85rem', color: '#344054' }}>
                                    Arrastrá o <span style={{ color: '#1570ef' }}>hacé clic</span> para seleccionar
                                </span>
                                <br />
                                <span style={{ fontSize: '0.75rem', color: '#98a2b3' }}>PDF, JPG, PNG</span>
                            </div>
                            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
                                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

                            {pendingFiles.length > 0 && (
                                <div className="flex flex-column gap-1 mt-2">
                                    {pendingFiles.map((f, i) => (
                                        <div key={i} style={fileRow}>
                                            <i className="pi pi-file" style={{ color: '#12b76a' }} />
                                            <span style={{ flex: 1, fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                            <span style={{ fontSize: '0.75rem', color: '#98a2b3' }}>{formatSize(f.size)}</span>
                                            <button type="button" onClick={() => removePending(i)} style={xBtn}>
                                                <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="w-full flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label={saving ? 'Guardando...' : 'Guardar'} icon="pi pi-save" type="submit" disabled={saving} />
                        </div>

                    </div>
                </form>
            </Dialog>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={visitas}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        showGridlines
                        responsiveLayout="scroll"
                        emptyMessage="No tenés visitas registradas"
                        className="p-datatable-gridlines bg-white"
                        sortField="fechaVisita"
                        sortOrder={-1}
                    >
                        <Column field="codigoUF"        header={t.UF}          sortable style={{ width: '8rem' }} />
                        <Column field="fechaVisita"     header="Fecha"       sortable body={fmtFecha} style={{ width: '11rem' }} />
                        <Column field="nombreVisitante" header="Visitante"   sortable />
                        <Column field="tipoVisita"      header="Tipo"        sortable style={{ width: '9rem' }} />
                        <Column field="patente"         header="Patente"     style={{ width: '8rem' }} />
                        <Column field="empresa"         header="Empresa"     />
                        <Column header="Venc. Seguro" style={{ width: '10rem' }} body={(row) => row.fechaVencimientoSeguro
                            ? <>{new Date(row.fechaVencimientoSeguro).toLocaleDateString('es-AR')}{vencidoBadge(row.fechaVencimientoSeguro)}</>
                            : <span style={{ color: '#aaa' }}>—</span>} />
                        <Column header="Venc. Registro" style={{ width: '10rem' }} body={(row) => row.fechaVencimientoRegistro
                            ? <>{new Date(row.fechaVencimientoRegistro).toLocaleDateString('es-AR')}{vencidoBadge(row.fechaVencimientoRegistro)}</>
                            : <span style={{ color: '#aaa' }}>—</span>} />
                        <Column header="Adj."           style={{ width: '5rem', textAlign: 'center' }} body={adjBadge} />
                        <Column
                            header="Acciones"
                            style={{ width: '8rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(row)} tooltip="Editar" />
                                    <Button icon="pi pi-trash"  rounded className="btn-delete"  onClick={() => handleDelete(row.visitaId)} tooltip="Cancelar visita" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.3rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const req = { color: '#f04438' };
const fileRow = { display: 'flex', alignItems: 'center', gap: '0.6rem', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.5rem 0.75rem', background: '#f9fafb' };
const xBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#98a2b3', padding: '0.2rem', lineHeight: 1 };

export default Visitas;
